export type CameraMode = 'side' | 'third' | 'fps';

export type GameMode = 'menu' | 'tutorial' | 'bout' | 'paused' | 'result' | 'editor';

export type BoutMode = 'vs_ai' | 'local_2p';

export type PlayerSlot = 0 | 1;

export type BladeAction =
  | 'idle'
  | 'advance'
  | 'retreat'
  | 'lunge'
  | 'fleche'
  | 'parryHigh'
  | 'parryLow'
  | 'riposte'
  | 'recover';

export type TargetZone = 'lame' | 'off' | 'floor' | 'none';

export type PriorityOwner = 'none' | 'player' | 'opponent';

/** Team / kit accent colors (lamé wash + equipment trim). */
export type FencerColorId =
  | 'blue'
  | 'red'
  | 'gold'
  | 'green'
  | 'purple'
  | 'cyan'
  | 'white'
  | 'black';

export interface FencerColorDef {
  id: FencerColorId;
  label: string;
  /** Lamé base tint */
  lame: number;
  /** Trim / accents (cuffs, zipper, grip stripe, ring) */
  accent: number;
  /** Emissive tip / highlights */
  glow: number;
  /** HUD / UI swatch */
  ui: string;
}

export const FENCER_COLORS: Record<FencerColorId, FencerColorDef> = {
  blue: { id: 'blue', label: 'Azure', lame: 0xb0c4de, accent: 0x3d7bff, glow: 0x2244aa, ui: '#3d9fff' },
  red: { id: 'red', label: 'Crimson', lame: 0xc9a0a0, accent: 0xff3355, glow: 0xaa2233, ui: '#ff4d5e' },
  gold: { id: 'gold', label: 'Gold', lame: 0xd4c4a0, accent: 0xf0c14b, glow: 0xaa8800, ui: '#f0c14b' },
  green: { id: 'green', label: 'Forest', lame: 0xa8c4b0, accent: 0x2ecc71, glow: 0x1a6644, ui: '#3dff9a' },
  purple: { id: 'purple', label: 'Violet', lame: 0xb8a8c8, accent: 0xa855f7, glow: 0x5522aa, ui: '#c084fc' },
  cyan: { id: 'cyan', label: 'Ice', lame: 0xa8d4d8, accent: 0x22d3ee, glow: 0x0e7490, ui: '#6ee7ff' },
  white: { id: 'white', label: 'Ivory', lame: 0xe8e6e0, accent: 0xd0ccc4, glow: 0x888888, ui: '#e8eefc' },
  black: { id: 'black', label: 'Obsidian', lame: 0x6a6e78, accent: 0x1a1c22, glow: 0x333344, ui: '#8b9bb8' },
};

export interface BoutState {
  playerScore: number;
  opponentScore: number;
  targetScore: number;
  timeLeft: number;
  priority: PriorityOwner;
  phase: 'en_garde' | 'fencing' | 'halt' | 'end';
  lastTouchBy: PriorityOwner;
  lastTouchValid: boolean;
}

export interface FencerState {
  x: number;
  facing: 1 | -1;
  stance: number;
  bladeAngle: number;
  action: BladeAction;
  actionT: number;
  actionDur: number;
  invuln: number;
  energy: number;
  hasPriority: boolean;
  canRiposte: boolean;
  isPlayer: boolean;
  colorId: FencerColorId;
}

/** Per-frame control sample for one fencer. */
export interface ControlSample {
  move: number;
  lunge: boolean;
  fleche: boolean;
  parry: 0 | 1 | -1;
  riposte: boolean;
}

export interface KeybindRow {
  action: string;
  p1: string;
  p2: string;
  pad: string;
}
